import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Calendar, User, MessageSquare, TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { apiService, SearchContentParams, SearchContentResponse } from '../../services/api';
import ApiErrorBanner from './ApiErrorBanner';

const ContentSearch: React.FC = () => {
  const [urlSearchParams] = useSearchParams();
  const [searchParams, setSearchParams] = useState<SearchContentParams>({
    query: '',
    ticker: '',
    sentiment: undefined,
    prediction: undefined,
    author: '',
    content_type: undefined,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  
  const [results, setResults] = useState<SearchContentResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Обработка URL параметров при загрузке компонента
  useEffect(() => {
    const tickerFromUrl = urlSearchParams.get('ticker');
    const queryFromUrl = urlSearchParams.get('query');
    const authorFromUrl = urlSearchParams.get('author');
    
    if (tickerFromUrl || queryFromUrl || authorFromUrl) {
      const newParams = { ...searchParams };
      if (tickerFromUrl) newParams.ticker = tickerFromUrl.toUpperCase();
      if (queryFromUrl) newParams.query = queryFromUrl;
      if (authorFromUrl) newParams.author = authorFromUrl;
      
      setSearchParams(newParams);
      
      // Автоматически выполняем поиск, если есть параметры
      if (tickerFromUrl || queryFromUrl || authorFromUrl) {
        performSearchWithParams(newParams);
      }
    }
  }, [urlSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const performSearchWithParams = async (params: SearchContentParams) => {
    setIsSearching(true);
    setError(null);
    
    try {
      const response = await apiService.searchContent(params);
      setResults(response);
    } catch (error) {
      console.error('Error searching content:', error);
      setError('Failed to search content. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const performSearch = async () => {
    if (!searchParams.query && !searchParams.ticker && !searchParams.author) {
      setError('Please enter a search query, ticker, or author');
      return;
    }

    setIsSearching(true);
    setError(null);
    
    try {
      const response = await apiService.searchContent(searchParams);
      setResults(response);
    } catch (error) {
      console.error('Error searching content:', error);
      setError('Failed to search content. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (field: keyof SearchContentParams, value: any) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSentimentColor = (sentiment: number) => {
    if (sentiment > 0.1) return 'text-[#4ff6e6]';
    if (sentiment < -0.1) return 'text-[#ff6b6b]';
    return 'text-[#D4AF37]';
  };

  const getSentimentIcon = (sentiment: number) => {
    if (sentiment > 0.1) return <TrendingUp className="w-4 h-4 text-[#4ff6e6]" />;
    if (sentiment < -0.1) return <TrendingDown className="w-4 h-4 text-[#ff6b6b]" />;
    return <Minus className="w-4 h-4 text-[#D4AF37]" />;
  };

  const getPredictionBadge = (prediction: string) => {
    switch (prediction.toLowerCase()) {
      case 'bullish':
        return <span className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20">Bullish</span>;
      case 'bearish':
        return <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full border border-red-500/20">Bearish</span>;
      case 'neutral':
        return <span className="px-2 py-1 bg-brand-gold/10 text-brand-gold text-xs rounded-full border border-brand-gold/20">Neutral</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/10 text-gray-400 text-xs rounded-full border border-gray-500/20">{prediction}</span>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="space-y-6">
      {error && <ApiErrorBanner message={error} />}
      
      {/* Search Input Section */}
      <div className="bg-[#1a1a1a] border border-[#222] rounded-xl shadow-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-[#4ff6e6]/10 rounded-lg flex items-center justify-center">
            <Search className="w-6 h-6 text-[#4ff6e6]" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-white">Content Search</h2>
            <p className="text-sm text-[#bbb]">Search through market sentiment content</p>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* Main Search Input */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={searchParams.query || ''}
              onChange={(e) => handleInputChange('query', e.target.value)}
              placeholder="Search for content, keywords, or phrases..."
              className="input-glass flex-1 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#4ff6e6] focus:border-transparent"
            />
            <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn btn-outline flex items-center space-x-2 flex-1 sm:flex-initial"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </button>
            <button
              onClick={performSearch}
              disabled={isSearching}
              className="btn btn-gold flex items-center space-x-2 flex-1 sm:flex-initial disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <Search className="w-4 h-4" />
                </motion.div>
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span>{isSearching ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-[#222] border border-[#333] rounded-lg p-4 space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Ticker Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Ticker</label>
                  <input
                    type="text"
                    value={searchParams.ticker || ''}
                    onChange={(e) => handleInputChange('ticker', e.target.value.toUpperCase())}
                    placeholder="e.g., AAPL, TSLA"
                    className="input-glass w-full rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#4ff6e6] focus:border-transparent"
                  />
                </div>

                {/* Author Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Author</label>
                  <input
                    type="text"
                    value={searchParams.author || ''}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    placeholder="Username"
                    className="input-glass w-full rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#4ff6e6] focus:border-transparent"
                  />
                </div>

                {/* Sentiment Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Sentiment</label>
                  <select
                    value={searchParams.sentiment || ''}
                    onChange={(e) => handleInputChange('sentiment', e.target.value || undefined)}
                    className="w-full"
                  >
                    <option value="">Any</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>

                {/* Prediction Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Prediction</label>
                  <select
                    value={searchParams.prediction || ''}
                    onChange={(e) => handleInputChange('prediction', e.target.value || undefined)}
                    className="w-full"
                  >
                    <option value="">Any</option>
                    <option value="bullish">Bullish</option>
                    <option value="bearish">Bearish</option>
                    <option value="neutral">Neutral</option>
                  </select>
                </div>

                {/* Content Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Content Type</label>
                  <select
                    value={searchParams.content_type || ''}
                    onChange={(e) => handleInputChange('content_type', e.target.value || undefined)}
                    className="w-full"
                  >
                    <option value="">Any</option>
                    <option value="post">Posts</option>
                    <option value="comment">Comments</option>
                  </select>
                </div>

                {/* Limit Filter */}
                <div>
                  <label className="block text-sm font-medium text-[#bbb] mb-2">Results Limit</label>
                  <select
                    value={searchParams.limit || 20}
                    onChange={(e) => handleInputChange('limit', parseInt(e.target.value))}
                    className="w-full"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Results Section */}
      {results && (
        <div className="bg-[#1a1a1a] border border-[#222] rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-white">Search Results</h3>
              <p className="text-sm text-[#bbb]">
                Found {results.total} results
                {results.query && ` for "${results.query}"`}
              </p>
            </div>
            {results.filters && Object.keys(results.filters).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(results.filters).map(([key, value]) => (
                  <span key={key} className="px-2 py-1 bg-[#333] text-[#bbb] text-xs rounded-full">
                    {key}: {value}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            {results.results.map((result) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#222] border border-[#333] rounded-lg p-4 hover:border-[#444] transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      {getSentimentIcon(result.sentiment)}
                      <span className={`text-sm font-medium ${getSentimentColor(result.sentiment)}`}>
                        {result.sentiment > 0.1 ? 'Positive' : result.sentiment < -0.1 ? 'Negative' : 'Neutral'}
                      </span>
                    </div>
                    {getPredictionBadge(result.prediction)}
                    <span className="px-2 py-1 bg-[#333] text-[#bbb] text-xs rounded-full flex items-center space-x-1">
                      <MessageSquare className="w-3 h-3" />
                      <span>{result.content_type}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-[#bbb]">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(result.created_at)}</span>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <User className="w-4 h-4 text-[#bbb]" />
                    <span className="text-sm font-medium text-[#4ff6e6]">{result.author}</span>
                  </div>
                  <p className="text-white leading-relaxed">{truncateText(result.content)}</p>
                </div>

                {result.tickers && result.tickers.length > 0 && (
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-sm text-[#bbb]">Tickers:</span>
                    <div className="flex flex-wrap gap-1">
                      {result.tickers.map((ticker) => (
                        <span key={ticker} className="px-2 py-1 bg-[#4ff6e6]/10 text-[#4ff6e6] text-xs rounded-full border border-[#4ff6e6]/20">
                          {ticker}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-[#bbb]">
                    {result.score && (
                      <span>Score: {result.score.toFixed(2)}</span>
                    )}
                    {result.subreddit && (
                      <span>r/{result.subreddit}</span>
                    )}
                  </div>
                  {result.url && (
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-[#4ff6e6] hover:text-[#33e6d6] transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span className="text-sm">View</span>
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {results.total > (results.limit || 20) && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#333]">
              <div className="text-sm text-[#bbb]">
                Showing {results.offset + 1} to {Math.min(results.offset + (results.limit || 20), results.total)} of {results.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const newOffset = Math.max(0, (results.offset || 0) - (results.limit || 20));
                    handleInputChange('offset', newOffset);
                    performSearch();
                  }}
                  disabled={(results.offset || 0) === 0}
                  className="btn btn-outline px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => {
                    const newOffset = (results.offset || 0) + (results.limit || 20);
                    handleInputChange('offset', newOffset);
                    performSearch();
                  }}
                  disabled={(results.offset || 0) + (results.limit || 20) >= results.total}
                  className="btn btn-outline px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentSearch; 
